import {Card, Container, Group, Stack, Title} from "@mantine/core";
import {useCallback, useMemo, useRef, useState} from "react";
import {
  addMilliseconds,
  adjustToTimeZone,
  adjustToTimeZoneSafely,
  driverActivitiesSelector,
  DriverActivityEntity,
  extractApiErrorMessage,
  getObjectId,
  PersonEntity,
  startOfDay,
  TransportSearchCriteria,
  useSearchDriverActivityQuery,
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {notifications} from "@mantine/notifications";
import {DriverActivityGrid} from "../../components/driver-activity-grid/DriverActivityGrid";
import {
  getDriverActivityPageCriteria,
  getDriverActivityPageFilter,
  getDriverActivityPageHiddenPersons,
  getDriverActivityPageState
} from "../../store/ui/driver-activity-page/selectors";
import {useDisclosure} from "@mantine/hooks";
import {useDispatch} from "react-redux";
import {
  GridPreDestroyedEvent,
  GridReadyEvent,
  GridState,
  ModuleRegistry,
  RowSelectedEvent
} from "@ag-grid-community/core";
import {
  clearToggleDriverActivityPagePerson,
  setDriverActivityPageCriteria,
  setDriverActivityPageFilter,
  setDriverActivityPageState,
  toggleDriverActivityPagePerson
} from "../../store/ui/driver-activity-page/actions";
import {AgGridReact} from '@ag-grid-community/react';
import {ClientSideRowModelModule} from "@ag-grid-community/client-side-row-model";
import {useOtsUISelector} from "../../hooks/useOtsUISelector";
import {DriverActivityToolbar} from "../../components/toolbar/DriverActivityToolbar/DriverActivityToolbar";
import {DriverActivityMenu} from "./DriverActivityMenu/DriverActivityMenu";
import {
  DriverActivityFilterFormModal,
  DriverActivityFilterFormValues
} from "../../components/form/DriverActivityFilterForm/DriverActivityFilterForm";
import {IconUserBolt} from "@tabler/icons-react";
import {Badge} from "../../components/badge/Badge/Badge";

ModuleRegistry.registerModules([
  ClientSideRowModelModule,
]);

export type DriverActivityPageProps = {}

export const DriverActivityPage = (props: DriverActivityPageProps) => {

  const criteria = useOtsUISelector(getDriverActivityPageCriteria)
  const filter = useOtsUISelector(getDriverActivityPageFilter)
  const gridState = useOtsUISelector(getDriverActivityPageState)
  const hiddenPersons = useOtsUISelector(getDriverActivityPageHiddenPersons)

  const [filterOpened, filterHandler] = useDisclosure(false);

  const dispatch = useDispatch();
  const {regionId, preferences,} = useApp();
  const {t} = useTranslation(["common"]);

  const [initialState, setInitialState] = useState<GridState | undefined>(undefined)
  const [selectedRows, setSelectedRows] = useState<DriverActivityEntity[]>([])
  const gridRef = useRef<AgGridReact<DriverActivityEntity>>(null)

  const selectActivities = useMemo(() => {
    return driverActivitiesSelector();
  }, []);

  const searchArg: TransportSearchCriteria | typeof skipToken = useMemo(() => {
    if (criteria?.fromDttm && regionId) {
      return {
        fromDttm: adjustToTimeZone(criteria.fromDttm).getTime(),
        toDttm: adjustToTimeZoneSafely(criteria.toDttm)?.getTime(),
        startShiftReport: criteria.startShiftReport,
        regionId: regionId,
        showHoldings: false,
        showTabletHoldings: false,
      }
    }
    return skipToken
  }, [criteria, regionId])

  const {activities, isFetching, error, refetch} = useSearchDriverActivityQuery(searchArg, {
    selectFromResult: (result) => ({
      ...result,
      activities: selectActivities(result, {
        personId: getObjectId(filter.person),
        withCVDs: false,
      }),
      isFetching: result.isFetching,
      error: result.error,
    }),
  });

  const onSearch = useCallback((by: "shift" | "transport", fromDate: Date | undefined, toDate: Date | undefined) => {
    const startShiftReport = by === "shift"

    if (criteria && fromDate?.getTime() === criteria.fromDttm && toDate?.getTime() === criteria.toDttm && startShiftReport === criteria?.startShiftReport) {
      if (refetch) {
        refetch()
      }
    } else {
      dispatch(setDriverActivityPageCriteria({
        ...criteria ?? {},
        startShiftReport: startShiftReport,
        fromDttm: fromDate?.getTime(),
        toDttm: toDate?.getTime()
      }))
    }

    dispatch(clearToggleDriverActivityPagePerson())

  }, [criteria, refetch, dispatch]);

  if (error) {
    notifications.show({
      id: "meterFlowVolumeSearchError",
      title: t("common.error", "Error"),
      message: extractApiErrorMessage(error) ?? t("common.error", "Error"),
      color: "red",
      loading: false,
      autoClose: true,
    });
  } else {
    notifications.hide("meterFlowVolumeSearchError")
  }

  const onGridPreDestroyed = useCallback((event: GridPreDestroyedEvent<DriverActivityEntity>) => {
    dispatch(setDriverActivityPageState(event.state))
  }, [dispatch])

  const onGridReady = useCallback((event: GridReadyEvent<DriverActivityEntity>) => {
    setInitialState(gridState);
  }, [gridState])

  const onRowSelected = useCallback((event: RowSelectedEvent<DriverActivityEntity>) => {
    setSelectedRows((prev) => {
      const selections: DriverActivityEntity[] = [...prev]

      const data = event.data
      if (data) {
        const foundIndex = prev.findIndex(a => a.id === data.id)
        if (event.node.isSelected()) {
          if (foundIndex === -1) {
            selections.push(data)
          }
        } else {
          if (foundIndex >= 0) {
            selections.splice(foundIndex, 1)
          }
        }
      }


      return selections
    })
  }, [])

  const onPersonToggle = useCallback((person: PersonEntity) => {
    dispatch(toggleDriverActivityPagePerson(person.id))
  }, [dispatch])

  const onFilterSubmit = useCallback((filter: DriverActivityFilterFormValues) => {
    dispatch(setDriverActivityPageFilter(filter))
    filterHandler.close()
  }, [dispatch, filterHandler])

  return (
    <ThemedPage>
      {(classes) => (
        <ResizeableContent>
          {(size) => (
            <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
              <Stack h="100%" m="sm">
                <RouteBreadcrumbs/>
                <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                  <Group position="apart">
                    <Group spacing="xs">
                      <IconUserBolt/>
                      <Title order={4}>{t("common.page.driveractivity.title", "Driver Activity")}</Title>
                      <Badge color="blue">
                        Beta
                      </Badge>
                    </Group>
                    <DriverActivityMenu refetch={refetch} context={selectedRows}/>
                  </Group>
                  <DriverActivityToolbar
                    defaultValues={{
                      by: criteria?.startShiftReport ? "shift" : "transport",
                      fromDate: criteria?.fromDttm ? new Date(criteria.fromDttm) : addMilliseconds(startOfDay(new Date()), preferences?.reportDefaultTime ?? 0),
                      toDate: criteria?.toDttm ? new Date(criteria?.toDttm) : undefined,
                    }}
                    loading={isFetching}
                    filter={filter}
                    onFilterClick={() => filterHandler.open()}
                    onSubmit={onSearch}/>
                </Card>
                <DriverActivityGrid
                  ref={gridRef}
                  data={activities}
                  initialState={initialState}
                  onGridReady={onGridReady}
                  onGridPreDestroyed={onGridPreDestroyed}
                  onRowSelected={onRowSelected}
                  hiddenPersons={hiddenPersons}
                  onPersonToggle={onPersonToggle}
                />

              </Stack>

              <DriverActivityFilterFormModal
                title={t<string>("common.action.filter", "Filter", {ns: "common"})}
                mih={400}
                opened={filterOpened}
                onClose={filterHandler.close}
                onSubmit={onFilterSubmit}
                defaultValues={{
                  ...filter
                }}
              />

            </Container>
          )}
        </ResizeableContent>
      )}
    </ThemedPage>
  );
};
